import { DOMAIN_KEY, domainKey } from "../MPP-Common-SPA/utils/globalUtility";
import { URL_SEARCH_PARAMS } from "../MPP-Common-SPA/common/constants/urlParams";

const endpoint = process.env.REACT_APP_API_ENDPOINT ?? "";

const loginEndpoint = `${endpoint}/api/auth/samlLogin` ?? "/api/auth/samlLogin";
// Logout must target the same origin that owns the authentication cookie (matching the
// app's relative "/api" base). Using the absolute REACT_APP_API_ENDPOINT here sends logout
// to a different backend on bridged sub-domains, leaving the local session cookie intact.
// Locally, CRA's dev server only proxies non-HTML requests; a browser navigation to a
// relative path is never forwarded to the API, so use the absolute endpoint instead.
const samlLogoutBase = process.env.REACT_APP_IS_LOCALHOST === 'true'
  ? `${endpoint}/api/auth/samlLogout`
  : "/api/auth/samlLogout";
const logoutEndpoint = samlLogoutBase;
const samlLoginEndpoint = `${endpoint}/api/auth/samlLogin` ?? "/api/auth/samlLogin";
const samlLogoutEndpoint = samlLogoutBase;
const proxyLogoutEndpoint = `${endpoint}/api/auth/endProxySession` ?? "/api/auth/endProxySession";

export const getCurrentUrl = () => {
  return window.location.href;
};

// The domain key is stripped from the address bar once the program loads
// (cleanupDomainKeyFromUrl), after which the program is identified only by sessionStorage -
// which is per-tab and therefore empty when the user returns from an account-creation email.
// Re-attaching it here keeps the program identifiable in the post-sign-in return URL.
const withDomainKey = (url) => {
  const currentDomainKey = sessionStorage.getItem(DOMAIN_KEY) || domainKey;
  if (!currentDomainKey) {
    return url;
  }

  try {
    const targetUrl = new URL(url, window.location.origin);
    const hasDomainKey = Array.from(targetUrl.searchParams.keys()).some(
      (key) => key.toLowerCase() === URL_SEARCH_PARAMS.DomainKey,
    );

    if (!hasDomainKey) {
      targetUrl.searchParams.set(URL_SEARCH_PARAMS.DomainKey, currentDomainKey);
    }

    return targetUrl.toString();
  } catch {
    return url;
  }
};

export const getCurrentDomain = () => {
  return window.location.protocol + '//' + window.location.host;
}

export const getCurrentUrlWithoutPath = () => {
  return window.location.href.split("?")[0].split("/").slice(0, -1).join("/")
};

export const getLoggedOutEndpoint = () => {
  return (
    getCurrentDomain() +
    "/home?action=logout"
  );
};

export const getApplyEndpoint = () => {
  return (
    window.location.href.split("?")[0].split("/").slice(0, -1).join("/") +
    "/apply"
  );
};

export const getCurrentEndpoint = () => {
  return (
    window.location.href.split("?")[0].split("/").slice(0, -1).join("/") +
    "/apply"
  );
};

// Origin of the generic/public portal (e.g. payments.alberta.ca), derived from the
// configured API endpoint. Sign-in must complete on this origin so the SAML assertion
// and session cookie land there before the user is bridged to a program sub-domain.
const getPublicPortalOrigin = () => {
  // Locally the API (44318) and SPA (5106) run on different origins; treat the current
  // page origin as the portal origin so no bridgeReturn wrapping occurs.
  if (process.env.REACT_APP_IS_LOCALHOST === 'true') {
    return window.location.origin;
  }

  if (!endpoint) {
    return "";
  }

  try {
    return new URL(endpoint).origin;
  } catch {
    return "";
  }
};

// When sign-in is initiated from a program-specific sub-domain (a cross-domain portal
// such as evacuationpayment.alberta.ca), route the post-authentication callback through
// the public portal, carrying this sub-domain's full URL as a `bridgeReturn` marker
// (payments.alberta.ca/?bridgeReturn=<sub-domain url>). After authentication completes
// on the public portal, the bootstrap redirect returns the user to that exact page with a
// `bridgeSource` marker, and the session-bridge flow establishes the session here. When
// already on the public portal, the original callback is returned unchanged so the
// existing sign-in flow is unaffected.
export const resolveLoginCallbackUrl = (callBackUrl) => {
  const defaultCallback = withDomainKey(callBackUrl || getCurrentUrl());
  const publicPortalOrigin = getPublicPortalOrigin();

  if (
    !publicPortalOrigin ||
    publicPortalOrigin.toLowerCase() === getCurrentDomain().toLowerCase()
  ) {
    // Encode so any `?`/`&`/`#` in the callback URL survives as a single `callback=`
    // query value instead of being split into extra query parameters on the server.
    return encodeURIComponent(defaultCallback);
  }

  // Carry the whole URL, not just the origin, so the user returns to the page they were
  // on rather than the sub-domain's landing page.
  const bridgeReturn = encodeURIComponent(defaultCallback);
  return encodeURIComponent(`${publicPortalOrigin}/?bridgeReturn=${bridgeReturn}`);
};

export const getLoginEndPoint = (callBackUrl) => {
  return `${loginEndpoint}?callback=${resolveLoginCallbackUrl(callBackUrl)}`;
};

export const getLogoutEndPoint = (callBackUrl) => {
  return `${logoutEndpoint}?callback=${callBackUrl}`;
};

export const getSamlLoginEndPoint = (callBackUrl) => {
  return `${samlLoginEndpoint}?callback=${resolveLoginCallbackUrl(callBackUrl)}`;
};

export const getSamlLogoutEndPoint = (callBackUrl) => {
  return `${samlLogoutEndpoint}?callback=${callBackUrl}`;
};

export const getProxyLogoutEndPoint = (applicationId) => {
  return applicationId 
    ? `${proxyLogoutEndpoint}?applicationId=${applicationId}`
    : `${proxyLogoutEndpoint}`;
};

export const endProxyAndRedirect = async (applicationId, programId) => {
    let internalPortalUrl = process.env.REACT_APP_INTERNAL_PORTAL_URL;
    let targetUrl;
    if (applicationId) {
        targetUrl = `${internalPortalUrl}/summary/${applicationId}`;
    } else if (programId) {
        targetUrl = `${internalPortalUrl}/workspace/${programId}`;
    } else {
        targetUrl = `${internalPortalUrl}/workspace`;
    }

    try {
        // Send the kill signal in the background
        await fetch(getProxyLogoutEndPoint(applicationId), {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
            redirect: 'manual'
        });
    } catch (error) {
        console.error("Session termination network error, forcing redirect.", error);
    } finally {
        //React forces the redirect
        window.location.href = targetUrl;
    }
};